import { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, PaperDetail, PaperLite, PostDetail, PostVideo, RepoDetail, RepoLite } from '../../../services/api'
import { RepoComments } from './RepoComments'
import { RepoHistory } from './RepoHistory'
import { AssignTarget } from './AssignTaskModal'
import { Button, Spinner, LoadingContainer } from '../../../components/shared/StyledComponents'
import {
    BadgeRow,
    Chip,
    CardText,
    ExternalLink,
    MutedText,
    Sha,
    healthPalette,
    paletteFor,
    mentionQualifier,
    repoHref,
    repoIsGone,
    repoLabel,
    ESTADO_LABELS,
    ESTADO_COLORS,
} from './linkedinStyles'

// =============================================================================
// SOTA detail modal — opens when a card body is clicked.
// Reuses the RecommendationModal Overlay/Modal/ModalHeader/CloseButton pattern.
//
// Since the list is one list, this is where a repo and a paper are read in full.
// It therefore navigates: post → one of its repos → that repo's papers, with a
// back arrow, instead of forcing the reader out to a tab that no longer exists.
// =============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 95%;
  max-width: 760px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`

const ModalTitle = styled.h2`
  margin: 0;
  flex: 1;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  word-break: break-word;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const SectionLabel = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`

/** Prominent "Resumen (IA)" box. */
const SummaryBox = styled.div`
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text};
`

/** Scrollable prose box for the full post content. */
const ContentBox = styled.div`
  max-height: 240px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.55;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
`

/** Responsive grid of post media thumbnails. */
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`

/** A single clickable post image (opens full-size in a new tab). */
const PostImage = styled.a`
  display: block;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  line-height: 0;

  & img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
    transition: transform 0.2s;
  }

  &:hover img {
    transform: scale(1.04);
  }
`

/** Rendered-markdown container for the README excerpt. */
const MarkdownBox = styled.div`
  max-height: 280px;
  overflow-y: auto;
  word-break: break-word;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.55;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};

  & > *:first-child { margin-top: 0; }
  & > *:last-child { margin-bottom: 0; }
  & h1, & h2, & h3, & h4 { margin: 0.7em 0 0.35em; line-height: 1.25; }
  & h1 { font-size: 1.15rem; }
  & h2 { font-size: 1.05rem; }
  & h3 { font-size: 0.98rem; }
  & p { margin: 0.45em 0; }
  & ul, & ol { margin: 0.45em 0; padding-left: 1.3em; }
  & li { margin: 0.15em 0; }
  & blockquote {
    margin: 0.5em 0;
    padding-left: 0.8em;
    border-left: 3px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textMuted};
  }
  & code {
    background: rgba(0, 0, 0, 0.06);
    padding: 0.1em 0.3em;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.85em;
  }
  & pre {
    background: #1e1e1e;
    color: #eaeaea;
    padding: 0.7em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;
  }
  & pre code { background: none; padding: 0; }
  & a { color: ${({ theme }) => theme.colors.primary}; }
  & img { max-width: 100%; border-radius: 6px; }
  & table { border-collapse: collapse; margin: 0.5em 0; }
  & th, & td { border: 1px solid ${({ theme }) => theme.colors.border}; padding: 0.3em 0.5em; }
`

/** Stacked list of playable videos. */
const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

/** A native <video> player for a scraped post video. */
const VideoPlayer = styled.video`
  width: 100%;
  max-height: 360px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: #000;
`

/** Poster-only fallback when the video stream URL isn't directly playable. */
const VideoPoster = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  line-height: 0;

  & img {
    width: 100%;
    max-height: 360px;
    object-fit: cover;
    display: block;
  }
`

const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border-radius: 50%;
  font-size: 1.4rem;
  pointer-events: none;
`

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ItemRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const CommitRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.4rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text};
`

const RepoRefBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const RepoRefName = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

/** Opens another entity inside this same modal (post → repo → paper). Styled as
 *  a link because that is what it is; it is a button because it navigates
 *  within the modal rather than to a URL. */
const DrillLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  text-align: left;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const ErrorState = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`

/** Back arrow in the modal header; only rendered when there is somewhere to go back to. */
const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`

const fmtDate = (iso?: string | null): string => {
    if (!iso) return ''
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES')
}

const estadoLabel = (estado?: string | null): string =>
    (estado && ESTADO_LABELS[estado]) || estado || 'Sin estado'

const estadoColor = (estado?: string | null): { bg: string; color: string } =>
    (estado && ESTADO_COLORS[estado]) || { bg: '#ECEFF1', color: '#607D8B' }

export interface SotaDetailTarget {
    type: 'repo' | 'paper' | 'post'
    id: number
}

interface SotaDetailModalProps {
    target: SotaDetailTarget | null
    onClose: () => void
    /** Bumped by the page when something this modal renders changed underneath
     *  it — an assignment created from the button below, which opens on top of
     *  this overlay and leaves `repo.assignments` / `paper.assignments` stale. */
    reloadKey?: number
    /** Provided only for admins: assignment targets a repo or a paper, so the
     *  button lives next to the one being read, never on the post as a whole. */
    onAssign?: (target: AssignTarget) => void
}

export const SotaDetailModal: React.FC<SotaDetailModalProps> = ({ target, reloadKey = 0, onClose, onAssign }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [repo, setRepo] = useState<RepoDetail | null>(null)
    const [paper, setPaper] = useState<PaperDetail | null>(null)
    const [post, setPost] = useState<PostDetail | null>(null)
    // Drill-down trail. The last entry is what is on screen; anything before it
    // is where the back arrow returns to.
    const [trail, setTrail] = useState<SotaDetailTarget[]>([])

    // A new target from the outside starts a new trail; closing empties it.
    useEffect(() => {
        setTrail(target ? [target] : [])
    }, [target])

    const current = trail[trail.length - 1] || null

    useEffect(() => {
        if (!current) return
        let cancelled = false
        setLoading(true)
        setError(null)
        setRepo(null)
        setPaper(null)
        setPost(null)

        const fetcher =
            current.type === 'repo'
                ? api.linkedin.getRepo(current.id).then((res) => { if (!cancelled) setRepo(res.data) })
                : current.type === 'paper'
                    ? api.linkedin.getPaper(current.id).then((res) => { if (!cancelled) setPaper(res.data) })
                    : api.linkedin.getPost(current.id).then((res) => { if (!cancelled) setPost(res.data) })

        fetcher
            .catch((err: any) => {
                if (!cancelled) setError(err?.response?.data?.detail || 'No se pudo cargar el detalle.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => { cancelled = true }
    }, [current, reloadKey])

    if (!target || !current) return null

    const push = (next: SotaDetailTarget) => setTrail((prev) => [...prev, next])
    const back = () => setTrail((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))

    const title = repo
        ? repoLabel(repo)
        : paper
            ? paper.title
            : post
                ? post.author || 'Publicación'
                : current.type === 'repo'
                    ? 'Repositorio'
                    : current.type === 'paper'
                        ? 'Paper'
                        : 'Publicación'

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    {trail.length > 1 && (
                        <BackButton onClick={back} aria-label="Volver">&larr;</BackButton>
                    )}
                    <ModalTitle>{title}</ModalTitle>
                    <CloseButton onClick={onClose} aria-label="Cerrar">&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    {loading ? (
                        <LoadingContainer>
                            <Spinner />
                        </LoadingContainer>
                    ) : error ? (
                        <ErrorState>{error}</ErrorState>
                    ) : repo ? (
                        <RepoDetailBody repo={repo} onOpen={push} onAssign={onAssign} />
                    ) : paper ? (
                        <PaperDetailBody paper={paper} onOpen={push} onAssign={onAssign} />
                    ) : post ? (
                        <PostDetailBody post={post} onOpen={push} onAssign={onAssign} />
                    ) : null}
                </ModalBody>
            </Modal>
        </Overlay>
    )
}

/** "Asignar a interno", rendered only when the caller supplied a handler. */
const AssignButton: React.FC<{
    onAssign?: (target: AssignTarget) => void
    target: AssignTarget
}> = ({ onAssign, target }) => {
    if (!onAssign) return null
    return (
        <Button variant="primary" size="sm" onClick={() => onAssign(target)}>
            Asignar a interno
        </Button>
    )
}

// -- Resumen (IA) shared block ------------------------------------------------

const SummarySection: React.FC<{ summary?: string | null }> = ({ summary }) => (
    <Section>
        <SectionLabel>Resumen (IA)</SectionLabel>
        {summary ? (
            <SummaryBox>{summary}</SummaryBox>
        ) : (
            <MutedText>Sin resumen generado</MutedText>
        )}
    </Section>
)

// -- Media (images + videos) shared block -------------------------------------

const MediaSection: React.FC<{
    images?: (string | null | undefined)[] | null
    videos?: (PostVideo | null | undefined)[] | null
    label?: string
}> = ({ images, videos, label = 'Imágenes y vídeos' }) => {
    const imgs = Array.from(new Set((images || []).filter((s): s is string => !!s)))
    const vids = (videos || []).filter((v): v is PostVideo => !!v && !!(v.url || v.poster))
    if (imgs.length === 0 && vids.length === 0) return null
    return (
        <Section>
            <SectionLabel>{label}</SectionLabel>
            {vids.length > 0 && (
                <VideoList>
                    {vids.map((v, i) =>
                        v.url ? (
                            <VideoPlayer key={`v-${i}`} controls preload="none" poster={v.poster || undefined}>
                                <source src={v.url} />
                            </VideoPlayer>
                        ) : (
                            <VideoPoster key={`vp-${i}`}>
                                <img src={v.poster as string} alt={`Vídeo ${i + 1}`} loading="lazy" />
                                <PlayBadge>▶</PlayBadge>
                            </VideoPoster>
                        )
                    )}
                </VideoList>
            )}
            {imgs.length > 0 && (
                <ImageGrid>
                    {imgs.map((src, i) => (
                        <PostImage key={`${src}-${i}`} href={src} target="_blank" rel="noopener noreferrer">
                            <img src={src} alt={`Imagen ${i + 1}`} loading="lazy" />
                        </PostImage>
                    ))}
                </ImageGrid>
            )}
        </Section>
    )
}

/** Aggregate media from a set of associated posts (repo/paper detail). */
const collectPostMedia = (
    posts?: import('../../../services/api').LinkedInPostLite[]
): { images: string[]; videos: PostVideo[] } => {
    const images: string[] = []
    const videos: PostVideo[] = []
    for (const p of posts || []) {
        for (const src of p.image_urls || []) if (src) images.push(src)
        for (const v of p.video_urls || []) if (v && (v.url || v.poster)) videos.push(v)
    }
    return { images, videos }
}

// -- Posts shared block -------------------------------------------------------

const PostsSection: React.FC<{ posts?: import('../../../services/api').LinkedInPostLite[] }> = ({ posts }) => {
    if (!posts || posts.length === 0) return null
    return (
        <Section>
            <SectionLabel>Publicaciones asociadas</SectionLabel>
            <ItemList>
                {posts.map((p) => (
                    <ItemRow key={p.id}>
                        {p.author && <strong>{p.author}</strong>}
                        {(p.summary || p.content) && <CardText>{p.summary || p.content}</CardText>}
                        <ExternalLink href={p.linkedin_url} target="_blank" rel="noopener noreferrer">
                            Ver en LinkedIn ↗
                        </ExternalLink>
                    </ItemRow>
                ))}
            </ItemList>
        </Section>
    )
}

// -- Papers shared block ------------------------------------------------------

const PapersSection: React.FC<{
    papers?: PaperLite[]
    onOpen: (target: SotaDetailTarget) => void
    onAssign?: (target: AssignTarget) => void
}> = ({ papers, onOpen, onAssign }) => {
    if (!papers || papers.length === 0) return null
    return (
        <Section>
            <SectionLabel>Papers asociados</SectionLabel>
            <ItemList>
                {papers.map((p) => (
                    <ItemRow key={p.id}>
                        <BadgeRow>
                            <DrillLink type="button" onClick={() => onOpen({ type: 'paper', id: p.id })}>
                                {p.title}
                            </DrillLink>
                            {p.source && (
                                <Chip $bg={paletteFor(p.source).bg} $color={paletteFor(p.source).color}>
                                    {p.source}
                                </Chip>
                            )}
                            {p.external_id && <Chip>{p.external_id}</Chip>}
                            {/* Same provenance a repo carries: a paper reached this
                                post either from its body or from the author's own
                                comment, and the second rests on an identity match. */}
                            {(() => {
                                const qualifier = mentionQualifier(p.mention_type)
                                return qualifier ? (
                                    <Chip $bg="#ECEFF1" $color="#607D8B" title={qualifier.hint}>
                                        🔍 {qualifier.suffix}
                                    </Chip>
                                ) : null
                            })()}
                        </BadgeRow>
                        <BadgeRow>
                            {p.url && (
                                <ExternalLink href={p.url} target="_blank" rel="noopener noreferrer">
                                    Ver paper ↗
                                </ExternalLink>
                            )}
                            <AssignButton
                                onAssign={onAssign}
                                target={{ target_type: 'paper', target_id: p.id, target_title: p.title }}
                            />
                        </BadgeRow>
                    </ItemRow>
                ))}
            </ItemList>
        </Section>
    )
}

// -- Assignments shared block -------------------------------------------------

const AssignmentsSection: React.FC<{ assignments?: import('../../../services/api').LinkedInAssignmentLite[] }> = ({ assignments }) => {
    if (!assignments || assignments.length === 0) return null
    return (
        <Section>
            <SectionLabel>Asignaciones</SectionLabel>
            <ItemList>
                {assignments.map((a) => {
                    const c = estadoColor(a.estado)
                    return (
                        <ItemRow key={a.id}>
                            <BadgeRow>
                                <strong>{a.user_display_name || `Usuario #${a.user_id}`}</strong>
                                <Chip $bg={c.bg} $color={c.color}>{estadoLabel(a.estado)}</Chip>
                                {typeof a.progress_pct === 'number' && <Chip>{a.progress_pct}%</Chip>}
                            </BadgeRow>
                            {(a.fecha_inicio || a.fecha_fin) && (
                                <MutedText>
                                    {fmtDate(a.fecha_inicio)}
                                    {a.fecha_fin ? ` → ${fmtDate(a.fecha_fin)}` : ''}
                                </MutedText>
                            )}
                            {a.internal_repo_url && (
                                <MutedText>
                                    <a href={a.internal_repo_url} target="_blank" rel="noreferrer">
                                        Repo de trabajo
                                    </a>
                                </MutedText>
                            )}
                        </ItemRow>
                    )
                })}
            </ItemList>
        </Section>
    )
}

// -- Repo body ----------------------------------------------------------------

const RepoDetailBody: React.FC<{
    repo: RepoDetail
    onOpen: (target: SotaDetailTarget) => void
    onAssign?: (target: AssignTarget) => void
}> = ({ repo, onOpen, onAssign }) => {
    const commits = repo.commits || []
    const media = collectPostMedia(repo.posts)

    return (
        <>
            <SummarySection summary={repo.ai_description} />

            <Section>
                <BadgeRow>
                    {typeof repo.stars === 'number' && <Chip $bg="#FFF8E1" $color="#F57F17">⭐ {repo.stars}</Chip>}
                    {typeof repo.forks === 'number' && <Chip>🍴 {repo.forks}</Chip>}
                    {repo.language && (
                        <Chip $bg={paletteFor(repo.language).bg} $color={paletteFor(repo.language).color}>
                            {repo.language}
                        </Chip>
                    )}
                    {typeof repo.health_score === 'number' && (
                        <Chip $bg={healthPalette(repo.health_score).bg} $color={healthPalette(repo.health_score).color}>
                            ❤️ {repo.health_score}
                        </Chip>
                    )}
                    {repo.is_trending && !repoIsGone(repo) && (
                        <Chip $bg="#FCE4EC" $color="#C2185B">🔥 Tendencia</Chip>
                    )}
                    {repoIsGone(repo) && <Chip $bg="#FFEBEE" $color="#C62828">Ya no existe en GitHub</Chip>}
                    {repo.license && <Chip>{repo.license}</Chip>}
                    {repo.latest_release && <Chip $bg="#EDE7F6" $color="#4527A0">🏷️ {repo.latest_release}</Chip>}
                    {typeof repo.contributors_count === 'number' && <Chip>👥 {repo.contributors_count}</Chip>}
                </BadgeRow>
                <BadgeRow>
                    {/* Nothing to open: GitHub deleted or hid it, so the link is a
                        404. The commits and snapshots already collected are still
                        below — that is why the row is kept at all. */}
                    {repoIsGone(repo) ? (
                        <MutedText>
                            El repositorio ya no está disponible en GitHub; lo que se ve aquí es el
                            histórico recogido antes de que desapareciera.
                        </MutedText>
                    ) : (
                        <ExternalLink href={repoHref(repo)} target="_blank" rel="noopener noreferrer">
                            Ver repositorio ↗
                        </ExternalLink>
                    )}
                    <AssignButton
                        onAssign={onAssign}
                        target={{ target_type: 'repo', target_id: repo.id, target_title: repoLabel(repo) }}
                    />
                </BadgeRow>
            </Section>

            {repo.readme_excerpt && (
                <Section>
                    <SectionLabel>README</SectionLabel>
                    <MarkdownBox>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                a: ({ href, children }) => (
                                    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                                ),
                            }}
                        >{repo.readme_excerpt}</ReactMarkdown>
                    </MarkdownBox>
                </Section>
            )}

            <MediaSection images={media.images} videos={media.videos} />

            <PapersSection papers={repo.papers} onOpen={onOpen} onAssign={onAssign} />

            <PostsSection posts={repo.posts} />

            {commits.length > 0 && (
                <Section>
                    <SectionLabel>Últimos commits</SectionLabel>
                    <ItemList>
                        {commits.map((c) => (
                            <CommitRow key={c.sha}>
                                <Sha>{c.sha.slice(0, 7)}</Sha>
                                {c.message && <span>{c.message}</span>}
                                {c.committed_at && <MutedText>· {fmtDate(c.committed_at)}</MutedText>}
                            </CommitRow>
                        ))}
                    </ItemList>
                </Section>
            )}

            <AssignmentsSection assignments={repo.assignments} />

            <Section>
                <RepoHistory repoId={repo.id} />
            </Section>

            <Section>
                <RepoComments repoId={repo.id} />
            </Section>
        </>
    )
}

// -- Paper body ---------------------------------------------------------------

const PaperDetailBody: React.FC<{
    paper: PaperDetail
    onOpen: (target: SotaDetailTarget) => void
    onAssign?: (target: AssignTarget) => void
}> = ({ paper, onOpen, onAssign }) => {
    const repoName = paper.repo_owner && paper.repo_name ? `${paper.repo_owner}/${paper.repo_name}` : null
    const repoUrl =
        paper.repo_owner && paper.repo_name
            ? repoHref({ platform: paper.repo_platform, owner: paper.repo_owner, name: paper.repo_name })
            : null
    const media = collectPostMedia(paper.posts)

    return (
        <>
            <SummarySection summary={paper.repo_ai_description} />

            <Section>
                <BadgeRow>
                    {paper.source && (
                        <Chip $bg={paletteFor(paper.source).bg} $color={paletteFor(paper.source).color}>
                            {paper.source}
                        </Chip>
                    )}
                    {paper.external_id && <Chip>{paper.external_id}</Chip>}
                    {paper.created_at && <Chip>{fmtDate(paper.created_at)}</Chip>}
                </BadgeRow>
                <BadgeRow>
                    {paper.url && (
                        <ExternalLink href={paper.url} target="_blank" rel="noopener noreferrer">
                            Ver paper ↗
                        </ExternalLink>
                    )}
                    <AssignButton
                        onAssign={onAssign}
                        target={{ target_type: 'paper', target_id: paper.id, target_title: paper.title }}
                    />
                </BadgeRow>
            </Section>

            {repoName && (
                <Section>
                    <SectionLabel>Repositorio asociado</SectionLabel>
                    <RepoRefBox>
                        <BadgeRow>
                            {paper.repo_id ? (
                                <DrillLink type="button" onClick={() => onOpen({ type: 'repo', id: paper.repo_id as number })}>
                                    {repoName}
                                </DrillLink>
                            ) : (
                                <RepoRefName>{repoName}</RepoRefName>
                            )}
                            {repoUrl && (
                                <ExternalLink href={repoUrl} target="_blank" rel="noopener noreferrer">↗</ExternalLink>
                            )}
                            {typeof paper.repo_stars === 'number' && (
                                <Chip $bg="#FFF8E1" $color="#F57F17">⭐ {paper.repo_stars}</Chip>
                            )}
                            {typeof paper.repo_health_score === 'number' && (
                                <Chip
                                    $bg={healthPalette(paper.repo_health_score).bg}
                                    $color={healthPalette(paper.repo_health_score).color}
                                >
                                    ❤️ {paper.repo_health_score}
                                </Chip>
                            )}
                        </BadgeRow>
                        {paper.repo_ai_description && <CardText>{paper.repo_ai_description}</CardText>}
                    </RepoRefBox>
                </Section>
            )}

            <MediaSection images={media.images} videos={media.videos} />

            <PostsSection posts={paper.posts} />
            <AssignmentsSection assignments={paper.assignments} />
        </>
    )
}

// -- Post body ----------------------------------------------------------------

const PostDetailBody: React.FC<{
    post: PostDetail
    onOpen: (target: SotaDetailTarget) => void
    onAssign?: (target: AssignTarget) => void
}> = ({ post, onOpen, onAssign }) => {
    const repos: RepoLite[] = post.repos || []
    const date = fmtDate(post.published_at || post.created_at)

    return (
        <>
            <Section>
                <BadgeRow>
                    {post.category && (
                        <Chip $bg={paletteFor(post.category).bg} $color={paletteFor(post.category).color}>
                            {post.category}
                        </Chip>
                    )}
                    {post.language && <Chip>🌐 {post.language}</Chip>}
                    {typeof post.reactions === 'number' && <Chip $bg="#FFF8E1" $color="#F57F17">👍 {post.reactions}</Chip>}
                    {date && <Chip>{date}</Chip>}
                </BadgeRow>
                {post.author && <MutedText><strong>{post.author}</strong></MutedText>}
                {/* Who sent this into the bot's inbox. Recorded on ingestion and,
                    until now, never shown anywhere. */}
                {post.shared_by && post.shared_by.length > 0 && (
                    <MutedText>
                        Compartido por{' '}
                        {post.shared_by
                            .map((s) => s.sender_name)
                            .filter(Boolean)
                            .join(', ')}
                    </MutedText>
                )}
            </Section>

            {post.tags && post.tags.length > 0 && (
                <Section>
                    <BadgeRow>
                        {post.tags.map((t) => {
                            const p = paletteFor(t)
                            return <Chip key={t} $bg={p.bg} $color={p.color}>#{t}</Chip>
                        })}
                    </BadgeRow>
                </Section>
            )}

            <SummarySection summary={post.summary} />

            {post.content && (
                <Section>
                    <SectionLabel>Contenido</SectionLabel>
                    <ContentBox>{post.content}</ContentBox>
                </Section>
            )}

            <MediaSection images={post.image_urls} videos={post.video_urls} />

            {repos.length > 0 && (
                <Section>
                    <SectionLabel>Repositorios asociados</SectionLabel>
                    <ItemList>
                        {repos.map((r) => (
                            <ItemRow key={r.id}>
                                <BadgeRow>
                                    <DrillLink type="button" onClick={() => onOpen({ type: 'repo', id: r.id })}>
                                        {repoLabel(r)}
                                    </DrillLink>
                                    {/* No link for a repo GitHub has deleted or made
                                        private: the href is a 404, and offering it
                                        here contradicts both the badge below it and
                                        the repo detail, which already says the page
                                        is gone and only the history remains. */}
                                    {!repoIsGone(r) && (
                                        <ExternalLink href={repoHref(r)} target="_blank" rel="noopener noreferrer">
                                            ↗
                                        </ExternalLink>
                                    )}
                                    {typeof r.stars === 'number' && <Chip $bg="#FFF8E1" $color="#F57F17">⭐ {r.stars}</Chip>}
                                    {r.language && (
                                        <Chip $bg={paletteFor(r.language).bg} $color={paletteFor(r.language).color}>
                                            {r.language}
                                        </Chip>
                                    )}
                                    {typeof r.health_score === 'number' && (
                                        <Chip
                                            $bg={healthPalette(r.health_score).bg}
                                            $color={healthPalette(r.health_score).color}
                                        >
                                            ❤️ {r.health_score}
                                        </Chip>
                                    )}
                                    {r.is_trending && !repoIsGone(r) && (
                                        <Chip $bg="#FCE4EC" $color="#C2185B">🔥 Tendencia</Chip>
                                    )}
                                    {/* A repo GitHub has deleted or made private is kept for
                                        history, but the list that hides those cannot hide this
                                        one: it is why the post is in the feed. */}
                                    {repoIsGone(r) && (
                                        <Chip $bg="#FFEBEE" $color="#C62828">Ya no existe en GitHub</Chip>
                                    )}
                                    {/* Either the post never wrote this URL and we matched a
                                        project name, or its author wrote it in their own
                                        comment. Say so where the attribution is made, not
                                        only on the card. */}
                                    {(() => {
                                        const qualifier = mentionQualifier(r.mention_type)
                                        return qualifier ? (
                                            <Chip $bg="#ECEFF1" $color="#607D8B" title={qualifier.hint}>
                                                🔍 {qualifier.suffix}
                                            </Chip>
                                        ) : null
                                    })()}
                                </BadgeRow>
                                {r.ai_description && <CardText>{r.ai_description}</CardText>}
                                <BadgeRow>
                                    <AssignButton
                                        onAssign={onAssign}
                                        target={{ target_type: 'repo', target_id: r.id, target_title: repoLabel(r) }}
                                    />
                                </BadgeRow>
                            </ItemRow>
                        ))}
                    </ItemList>
                </Section>
            )}

            <PapersSection papers={post.papers} onOpen={onOpen} onAssign={onAssign} />

            {/* The list badges say "nothing attached"; the modal has to agree. */}
            {repos.length === 0 && (post.papers || []).length === 0 && (
                <Section>
                    <MutedText>Esta publicación no tiene repositorio ni paper asociados.</MutedText>
                </Section>
            )}

            {post.linkedin_url && (
                <Section>
                    <ExternalLink href={post.linkedin_url} target="_blank" rel="noopener noreferrer">
                        Ver en LinkedIn ↗
                    </ExternalLink>
                </Section>
            )}
        </>
    )
}

export default SotaDetailModal
